/**
 * 收斂抽獎回流頁輪詢的三個入口為單一排程器：單一 timer、單一 in-flight 旗標，
 * 所有副作用（ajax/setTimeout）由呼叫端透過 deps 注入，這裡只管狀態機。
 * @param {{
 *   poll: function(number, number): Promise<{terminal: boolean, nextDelayMs: number}>,
 *   schedule: function(function(): void, number): *,
 *   cancel: function(*): void,
 *   now: function(): number
 * }} deps
 * @returns {{
 *   start: function(number): boolean,
 *   wake: function(): void,
 *   stop: function(): void,
 *   isCurrent: function(number): boolean
 * }}
 */
export function createDrawPollScheduler({ poll, schedule, cancel, now }) {
    let currentOrderId = null;
    let startedAt = null;
    let timerHandle = null;
    let inFlight = false;
    let pending = false;
    let terminal = true;
    let queuedOrderId = null;

    function clearTimer() {
        if (timerHandle !== null) {
            cancel(timerHandle);
            timerHandle = null;
        }
    }

    function runNow() {
        // 歸零而非留著過期 handle：進到這裡有兩種來源——schedule() 排的那顆已自然觸發
        // （此時它已不代表任何待命 timer），或呼叫端已在呼叫前自行 clearTimer()。兩種
        // 情況 timerHandle 都該是 null，維持「非 null ⇔ 有待命 timer 可取消」這條不變式。
        timerHandle = null;
        inFlight = true;
        const orderId = currentOrderId;
        const orderStartedAt = startedAt;
        Promise.resolve(poll(orderId, orderStartedAt)).then(
            (result) => settle(result),
            (err) => {
                // poll 契約上不該 reject（runner 自己吃掉 ajax 失敗）——會走到這裡代表
                // runner 本身壞了，靜默無限重試會把 bug 藏起來，故視同終局停止
                console.error('[wtoy] draw poll scheduler: poll() rejected', err);
                settle({ terminal: true, nextDelayMs: 0 });
            }
        );
    }

    function settle(result) {
        inFlight = false;
        if (result.terminal) {
            stop();
        }
        if (queuedOrderId !== null) {
            const nextOrderId = queuedOrderId;
            queuedOrderId = null;
            beginOrder(nextOrderId);
            return;
        }
        if (terminal) {
            return;
        }
        if (pending) {
            pending = false;
            runNow();
            return;
        }
        timerHandle = schedule(runNow, result.nextDelayMs);
    }

    function beginOrder(orderId) {
        clearTimer();
        currentOrderId = orderId;
        startedAt = now();
        terminal = false;
        pending = false;
        runNow();
    }

    function start(orderId) {
        if (orderId === currentOrderId) {
            if (terminal) {
                return false;
            }
            wake();
            return true;
        }
        if (inFlight) {
            queuedOrderId = orderId;
            clearTimer();
            return true;
        }
        beginOrder(orderId);
        return true;
    }

    /**
     * `orderId` 是否為目前唯一在追蹤、且沒有換單排隊中的單。換單交棒（見 start()）刻意
     * 不中止在飛的舊單請求，讓它正常 settle——但那次回應的副作用（清狀態、渲染結果彈
     * 窗）不該對已經換到新單的使用者生效。呼叫端（runner）在執行任何副作用前應先以此
     * 判斷這次回應是否仍屬當前單，不是就只回終局、不做任何副作用。
     * @param {number} orderId
     * @returns {boolean}
     */
    function isCurrent(orderId) {
        return orderId === currentOrderId && queuedOrderId === null;
    }

    function wake() {
        if (currentOrderId === null || terminal) {
            return;
        }
        clearTimer();
        if (!inFlight) {
            runNow();
        } else {
            pending = true;
        }
    }

    // 對外仍 export：目前 production 端無呼叫者（settle() 內部與單測才用），
    // 讓終局行為可在單測直接驅動，不是死碼。
    function stop() {
        terminal = true;
        pending = false;
        clearTimer();
    }

    return { start, wake, stop, isCurrent };
}
